import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileText,
  Search,
  Calendar,
  User,
  ArrowUpRight,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/SupabaseAuthContext';

export const AnalysesListPage = () => {
  const { isDemo } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analysesRes, patientsRes] = await Promise.all([
        apiFetch('/analyses'),
        apiFetch('/patients'),
      ]);

      if (analysesRes.ok) {
        const analysesData = await analysesRes.json();
        setAnalyses(analysesData);
      }

      if (patientsRes.ok) {
        const patientsData = await patientsRes.json();
        const patientsMap = {};
        patientsData.forEach(p => {
          patientsMap[p.patient_id] = p;
        });
        setPatients(patientsMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (analysisId, e) => {
    e.stopPropagation();
    try {
      const response = await apiFetch(`/report/${analysisId}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pranagen_report_${analysisId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Report downloaded');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const filteredAnalyses = analyses.filter(analysis => {
    const patient = patients[analysis.patient_id];
    if (!patient) return false;
    
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
    return (
      fullName.includes(searchLower) ||
      patient.patient_id_number.toLowerCase().includes(searchLower) ||
      analysis.collection_date.includes(searchQuery)
    );
  });

  return (
    <div className="space-y-6" data-testid="analyses-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Analyses</h1>
          <p className="text-slate-600 mt-1">View all biological age analyses.</p>
        </div>
        {!isDemo && (
          <Link to="/upload">
            <Button className="bg-[#196a70] hover:bg-[#12565b] text-white">
              New Analysis
            </Button>
          </Link>
        )}
      </div>

      {/* Search */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by patient name, ID, or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-analyses"
            />
          </div>
        </CardContent>
      </Card>

      {/* Analyses Table */}
      {loading ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filteredAnalyses.length > 0 ? (
        <Card className="bg-white border-slate-200 shadow-soft overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Patient</TableHead>
                <TableHead>Collection Date</TableHead>
                <TableHead className="text-right">Chrono Age</TableHead>
                <TableHead className="text-right">PhenoAge</TableHead>
                <TableHead className="text-right">Bortz Age</TableHead>
                <TableHead className="text-right">Pranagen Age</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAnalyses.map((analysis) => {
                const patient = patients[analysis.patient_id];
                return (
                  <TableRow
                    key={analysis.analysis_id}
                    className="hover:bg-slate-50 cursor-pointer"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[#dbe9e5] flex items-center justify-center">
                          <User className="w-4 h-4 text-[#196a70]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {patient ? `${patient.first_name} ${patient.last_name}` : '—'}
                          </p>
                          <p className="text-xs text-slate-500 font-mono">
                            {patient?.patient_id_number}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatDate(analysis.collection_date)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {analysis.chronological_age?.toFixed(1)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {analysis.pheno_age?.toFixed(1) || '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {analysis.bortz_age?.toFixed(1) || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-mono font-semibold text-[#196a70]">
                        {analysis.pranagen_clock?.toFixed(1) || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDownloadReport(analysis.analysis_id, e)}
                          className="text-slate-600"
                          data-testid={`download-${analysis.analysis_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Link to={`/analyses/${analysis.analysis_id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#196a70]"
                            data-testid={`view-${analysis.analysis_id}`}
                          >
                            <ArrowUpRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading font-medium text-slate-900 mb-1">
              {searchQuery ? 'No analyses found' : 'No analyses yet'}
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              {searchQuery
                ? 'Try adjusting your search query.'
                : 'Upload blood work to create your first analysis.'}
            </p>
            {!searchQuery && !isDemo && (
              <Link to="/upload">
                <Button className="bg-[#196a70] hover:bg-[#12565b] text-white">
                  Upload Blood Work
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
